import { useCallback, useMemo, useState } from 'react'

import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import GetAppIcon from '@material-ui/icons/GetApp'

import {
  HTML_BLOCK_POLICY,
  HTML_BLOCK_REFERRER_POLICY,
  HTML_BLOCK_SANDBOX,
} from '../../consts/html-block-policy'
import useHtmlBlockFrame from '../../hooks/use-html-block-frame'
import { buildSrcDoc } from '../../utils/html-block/frame-document-builder'
import {
  getUtf8ByteLength,
  hasRenderableHtml,
  isFullDocument,
  sanitizeHtmlSnippet,
} from '../../utils/html-block/snippet-sanitizer'
import CodeBlock from './code-block'
import HtmlBlockPlaceholder from './html-block-placeholder'
import { useHtmlBlockRuntime } from './html-block-runtime'

type HtmlBlockFrameProps = {
  source: string
}

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(1.5, 0),
    width: '100%',
  },
  toolbar: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    height: 32,
    minHeight: 32,
    borderRadius: 8,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-surface, #ffffff)',
    padding: theme.spacing(0, 1.5),
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ds-fg-secondary, #535b6c)',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
    cursor: 'pointer',
    transition: 'background-color 200ms ease-out, border-color 200ms ease-out, box-shadow 200ms ease-out, color 200ms ease-out, transform 200ms ease-out',
    '&:hover:not(:disabled)': {
      transform: 'translateY(-1px)',
      borderColor: 'var(--ds-border-focus, #2f3f69)',
      background: 'var(--ds-bg-subtle-hover, rgba(241, 242, 247, 0.7))',
      color: 'var(--ds-fg-primary, #1f2430)',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: '0 0 0 2px var(--ds-border-focus, #2f3f69)',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(1px)',
      boxShadow: 'none',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
      boxShadow: 'none',
      transform: 'none',
    },
  },
  iconAction: {
    width: 32,
    padding: 0,
  },
  actionIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  previewShell: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 12,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-surface, #ffffff)',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
    transition: 'transform 200ms ease-out, border-color 200ms ease-out, box-shadow 200ms ease-out',
  },
  previewShellInteractive: {
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: 'var(--ds-border-focus, #2f3f69)',
      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.1)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
    },
    '&:focus-within': {
      borderColor: 'var(--ds-border-focus, #2f3f69)',
      boxShadow: '0 0 0 3px rgba(47, 63, 105, 0.28)',
    },
  },
  skeleton: {
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10,
    display: 'flex',
    minHeight: 80,
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
  },
  overlaySkeleton: {
    borderRadius: 12,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    animation: '$pulse 1600ms ease-in-out infinite',
  },
  frame: {
    display: 'block',
    width: '100%',
    border: 0,
    background: '#ffffff',
  },
  frameHidden: {
    opacity: 0,
  },
  previewTrigger: {
    position: 'absolute',
    top: -1,
    right: -1,
    bottom: -1,
    left: -1,
    zIndex: 20,
    display: 'block',
    cursor: 'zoom-in',
    borderRadius: 'inherit',
    border: 0,
    background: 'transparent',
    padding: 0,
    '&:focus': {
      outline: 'none',
    },
  },
  overlayHost: {
    position: 'relative',
    width: '100%',
  },
  overlayFrame: {
    width: '100%',
    borderRadius: 12,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: '#ffffff',
  },
  dialogPaper: {
    width: 'min(96vw, 1400px)',
    maxWidth: 'none',
    borderRadius: 24,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-elevated, #fff)',
    boxShadow: '0 24px 64px rgba(15, 23, 42, 0.26)',
  },
  dialogTitleRoot: {
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  dialogTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
  },
  dialogCloseButton: {
    padding: theme.spacing(0.5),
    borderRadius: 8,
    color: 'var(--ds-fg-secondary, #535b6c)',
    '&:hover': {
      background: 'var(--ds-bg-subtle, #f1f2f7)',
    },
  },
  dialogContent: {
    // The modal body is the only scroll region: the frame reports its full height.
    padding: theme.spacing(2.5, 3),
    maxHeight: '75vh',
    overflow: 'auto',
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.55 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.55 },
  },
}))

/**
 * Render one assistant-authored HTML snippet inside a sandboxed frame, with an
 * independent full-view overlay frame for the maximized preview.
 */
const HtmlBlockFrame = ({ source }: HtmlBlockFrameProps) => {
  const classes = useStyles()
  const { frameRegistry, visibility } = useHtmlBlockRuntime()

  const [frameId] = useState(() => frameRegistry.createFrameId())
  const [overlayFrameId] = useState(() => frameRegistry.createFrameId())
  const [overlayOpen, setOverlayOpen] = useState(false)

  const sourceTooLarge = useMemo(
    () => getUtf8ByteLength(source) > HTML_BLOCK_POLICY.limits.snippetBytes,
    [source]
  )

  const sanitizedSource = useMemo(
    () => sanitizeHtmlSnippet(sourceTooLarge ? '' : source),
    [source, sourceTooLarge]
  )

  const hasRenderableSource = useMemo(
    () => !sourceTooLarge && hasRenderableHtml(sanitizedSource),
    [sanitizedSource, sourceTooLarge]
  )

  const fullDocument = useMemo(() => isFullDocument(source), [source])

  const srcdoc = useMemo(() => {
    if (!hasRenderableSource) {
      return ''
    }

    return buildSrcDoc(sanitizedSource, fullDocument)
  }, [fullDocument, hasRenderableSource, sanitizedSource])

  const overlaySrcdoc = useMemo(() => {
    if (!hasRenderableSource) {
      return ''
    }

    return buildSrcDoc(sanitizedSource, fullDocument)
  }, [fullDocument, hasRenderableSource, sanitizedSource])

  const {
    frameRef,
    frameSrcdoc,
    frameHeight,
    hasReportedHeight,
    loaded,
    showSkeleton,
    handleLoad,
    handlePrint,
  } = useHtmlBlockFrame({
    frameId,
    frameRegistry,
    hasRenderableSource,
    srcdoc,
    visibility,
  })

  const {
    frameRef: overlayFrameRef,
    frameSrcdoc: overlayFrameSrcdoc,
    reportedHeight: overlayReportedHeight,
    showSkeleton: overlayShowSkeleton,
    handleLoad: overlayHandleLoad,
  } = useHtmlBlockFrame({
    frameId: overlayFrameId,
    frameRegistry,
    hasRenderableSource,
    srcdoc: overlaySrcdoc,
    visibility,
  })

  const previewReady = hasReportedHeight && !showSkeleton

  const openFullView = useCallback((): void => {
    setOverlayOpen(true)
  }, [])

  const closeFullView = useCallback((): void => {
    setOverlayOpen(false)
  }, [])

  if (!hasRenderableSource) {
    return sourceTooLarge ? (
      <HtmlBlockPlaceholder source={source} reason="too-large" />
    ) : (
      <CodeBlock code={source} language="html" />
    )
  }

  return (
    <>
      <div className={`html-block-frame ${classes.root}`}>
        <div className={classes.toolbar}>
          <button
            type="button"
            className={classes.action}
            aria-label="Export PDF"
            title="Export PDF"
            disabled={!loaded}
            onClick={handlePrint}
          >
            <GetAppIcon className={classes.actionIcon} />
            <span>Export PDF</span>
          </button>
          <button
            type="button"
            className={`${classes.action} ${classes.iconAction}`}
            aria-label="Open full HTML preview"
            title="Open full HTML preview"
            disabled={!previewReady}
            onClick={openFullView}
          >
            <FullscreenIcon className={classes.actionIcon} />
          </button>
        </div>

        <div
          className={`${classes.previewShell} ${
            previewReady ? classes.previewShellInteractive : ''
          }`}
        >
          {showSkeleton ? (
            <div className={classes.skeleton} aria-hidden="true">
              Loading preview…
            </div>
          ) : null}
          <iframe
            ref={frameRef}
            srcDoc={frameSrcdoc || undefined}
            height={frameHeight}
            className={`${classes.frame} ${showSkeleton ? classes.frameHidden : ''}`}
            sandbox={HTML_BLOCK_SANDBOX}
            referrerPolicy={HTML_BLOCK_REFERRER_POLICY}
            title="Rendered HTML content"
            tabIndex={-1}
            name={frameId}
            data-frame-id={frameId}
            onLoad={handleLoad}
          />
          {previewReady ? (
            <button
              type="button"
              className={classes.previewTrigger}
              aria-label="Open full HTML preview"
              onClick={openFullView}
            />
          ) : null}
        </div>
      </div>

      <Dialog
        open={overlayOpen}
        onClose={closeFullView}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby={`${frameId}-full-view-title`}
      >
        <DialogTitle disableTypography className={classes.dialogTitleRoot}>
          <Typography className={classes.dialogTitle} id={`${frameId}-full-view-title`}>
            Full HTML preview
          </Typography>
          <IconButton
            className={classes.dialogCloseButton}
            aria-label="Close modal"
            onClick={closeFullView}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.overlayHost}>
            {overlayShowSkeleton ? (
              <div
                className={`${classes.skeleton} ${classes.overlaySkeleton}`}
                aria-hidden="true"
              >
                Loading preview…
              </div>
            ) : null}
            <iframe
              ref={overlayFrameRef}
              srcDoc={overlayFrameSrcdoc || undefined}
              height={overlayReportedHeight}
              className={`${classes.overlayFrame} ${
                overlayShowSkeleton ? classes.frameHidden : ''
              }`}
              sandbox={HTML_BLOCK_SANDBOX}
              referrerPolicy={HTML_BLOCK_REFERRER_POLICY}
              title="Full rendered HTML content"
              name={overlayFrameId}
              data-frame-id={overlayFrameId}
              onLoad={overlayHandleLoad}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default HtmlBlockFrame
